import { useState, useMemo } from "react";
import { FolderInput, Link2, Trash2, X, FolderMinus, Loader2, Combine, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FolderSelector } from "@/components/knowledge-base";
import { Agent } from "@/types/agent/agent";
import { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";

interface BulkActionToolbarProps {
  selectedCount: number;
  agents: Partial<Agent>[];
  documents: DocumentWithMetadata[];
  selectedIds: Set<string>;
  onMoveToFolder: (folderId: string | null) => void;
  onAssignToGenie: (agent: Partial<Agent>) => void;
  onDelete: () => void;
  onRemoveFromFolder: () => void;
  onConsolidate: (docName?: string, deleteOriginals?: boolean) => void;
  onClearSelection: () => void;
  isMoving?: boolean;
  isDeleting?: boolean;
  isConsolidating?: boolean;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_PAGES = 100;

export const BulkActionToolbar = ({
  selectedCount,
  agents,
  documents,
  selectedIds,
  onMoveToFolder,
  onAssignToGenie,
  onDelete,
  onRemoveFromFolder,
  onConsolidate,
  onClearSelection,
  isMoving,
  isDeleting,
  isConsolidating,
}: BulkActionToolbarProps) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [folderOpen, setFolderOpen] = useState(false);
  const [genieOpen, setGenieOpen] = useState(false);
  const [consolidateOpen, setConsolidateOpen] = useState(false);
  const [consolidateName, setConsolidateName] = useState("");
  const [deleteAfterMerge, setDeleteAfterMerge] = useState(false);

  const selectedDocs = useMemo(() => {
    return documents.filter((doc) => selectedIds.has(doc.id));
  }, [documents, selectedIds]);

  const largeFiles = useMemo(() => {
    return selectedDocs.filter((doc) => {
      const fileSize = doc.metadata?.pdf_size || 0;
      const pageCount = doc.metadata?.total_pages || 0;
      return fileSize > MAX_FILE_SIZE || pageCount > MAX_PAGES;
    });
  }, [selectedDocs]);

  const canConsolidate = selectedCount >= 2 && largeFiles.length === 0;

  if (selectedCount === 0) return null;

  return (
    <>
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex justify-center pointer-events-none">
        <div className="inline-flex items-center gap-2 bg-white border border-genie-border shadow-xl rounded-xl px-4 py-2.5 pointer-events-auto">
          <span className="text-sm font-medium text-genie-text whitespace-nowrap">
            {selectedCount} selected
          </span>

          <div className="h-5 w-px bg-genie-border" />

          {/* Move to Folder */}
          <Popover open={folderOpen} onOpenChange={setFolderOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs gap-1.5 border-genie-border"
                disabled={isMoving}
              >
                {isMoving ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <FolderInput className="h-3.5 w-3.5" />
                )}
                Move to Folder
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2" align="center" side="top">
              <p className="text-xs font-medium text-gray-500 mb-2 px-1">Select folder</p>
              <FolderSelector
                value={undefined}
                onValueChange={(val) => {
                  onMoveToFolder(val === undefined ? null : val);
                  setFolderOpen(false);
                }}
                showAll={false}
                showUncategorized={false}
                className="w-full"
              />
            </PopoverContent>
          </Popover>

          {/* Remove from Folder */}
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs gap-1.5 border-genie-border"
            onClick={onRemoveFromFolder}
            disabled={isMoving}
          >
            {isMoving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <FolderMinus className="h-3.5 w-3.5" />
            )}
            Remove from Folder
          </Button>

          {/* Assign to Genie */}
          <Popover open={genieOpen} onOpenChange={setGenieOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs gap-1.5 border-genie-border"
              >
                <Link2 className="h-3.5 w-3.5" />
                Assign to Genie
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2" align="center" side="top">
              <p className="text-xs font-medium text-gray-500 mb-2 px-1">Select genie</p>
              <div className="max-h-48 overflow-y-auto space-y-0.5">
                {agents.map((agent) => (
                  <button
                    key={agent.id}
                    className="w-full text-left px-2 py-1.5 text-sm rounded-md hover:bg-genie-primary/10 transition-colors truncate"
                    onClick={() => {
                      onAssignToGenie(agent);
                      setGenieOpen(false);
                    }}
                  >
                    {agent.name}
                  </button>
                ))}
                {agents.length === 0 && (
                  <p className="text-xs text-gray-400 px-2 py-1">No genies available</p>
                )}
              </div>
            </PopoverContent>
          </Popover>

          {/* Consolidate */}
          <Popover open={consolidateOpen} onOpenChange={setConsolidateOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs gap-1.5 border-genie-border"
                disabled={isConsolidating || !canConsolidate}
                title={
                  selectedCount < 2
                    ? "Select at least 2 documents to consolidate"
                    : largeFiles.length > 0
                      ? `Cannot consolidate files larger than 5MB or with more than 100 pages (${largeFiles.length} selected)`
                      : ""
                }
              >
                {isConsolidating ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Combine className="h-3.5 w-3.5" />
                )}
                Consolidate
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-3" align="center" side="top">
              {largeFiles.length > 0 ? (
                <div className="space-y-2 text-sm">
                  <div className="flex gap-2 items-start text-orange-600">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium">Cannot consolidate large files</p>
                      <p className="text-xs text-gray-600 mt-1">
                        {largeFiles.length} selected file{largeFiles.length > 1 ? "s" : ""} exceed the size or page limit:
                      </p>
                      <ul className="text-xs text-gray-600 mt-1 space-y-0.5 list-disc list-inside">
                        {largeFiles.map((doc) => (
                          <li key={doc.id} className="truncate">
                            {doc.name}
                            {doc.metadata?.pdf_size && doc.metadata.pdf_size > MAX_FILE_SIZE
                              ? ` (${(doc.metadata.pdf_size / 1024 / 1024).toFixed(1)}MB)`
                              : doc.metadata?.total_pages && doc.metadata.total_pages > MAX_PAGES
                                ? ` (${doc.metadata.total_pages} pages)`
                                : ""}
                          </li>
                        ))}
                      </ul>
                      <p className="text-xs text-gray-600 mt-2">Limit: 5MB or 100 pages</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1.5">Consolidated document name (optional)</p>
                    <input
                      type="text"
                      placeholder="Leave blank for auto-name"
                      value={consolidateName}
                      onChange={(e) => setConsolidateName(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-genie-border rounded-md focus:outline-none focus:ring-1 focus:ring-genie-primary"
                    />
                  </div>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={deleteAfterMerge}
                      onChange={(e) => setDeleteAfterMerge(e.target.checked)}
                      className="h-3.5 w-3.5 rounded border-genie-border text-genie-primary focus:ring-1 focus:ring-genie-primary"
                    />
                    <span className="text-xs font-medium text-gray-700">Delete documents after merge</span>
                  </label>

                  <Button
                    size="sm"
                    className="w-full h-7 text-xs"
                    onClick={() => {
                      onConsolidate(consolidateName || undefined, deleteAfterMerge);
                      setConsolidateName("");
                      setDeleteAfterMerge(false);
                      setConsolidateOpen(false);
                    }}
                    disabled={isConsolidating}
                  >
                    Consolidate
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>

          <div className="h-5 w-px bg-genie-border" />

          {/* Delete */}
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs gap-1.5 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
            Delete
          </Button>

          <div className="h-5 w-px bg-genie-border" />

          {/* Clear Selection */}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-gray-400 hover:text-gray-600"
            onClick={onClearSelection}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedCount} document{selectedCount > 1 ? "s" : ""}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the selected document{selectedCount > 1 ? "s" : ""} and remove{" "}
              {selectedCount > 1 ? "them" : "it"} from any associated genies. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                onDelete();
                setShowDeleteConfirm(false);
              }}
            >
              Delete {selectedCount} document{selectedCount > 1 ? "s" : ""}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
